/**
 * Captures the source of a visit into a first-party cookie, so it survives
 * navigation and can be sent with the order. Two kinds of data go in: the
 * campaign parameters (`utm_source`, `utm_medium`, `utm_campaign`, `utm_term`,
 * `utm_content`, `gclid`) read from the landing URL, and the external referrer
 * together with the landing page. A new campaign visit overwrites the whole
 * previous campaign set (last touch) except the gclid, which only another ad
 * click replaces. The referrer, the landing page and the time of arrival
 * describe the first visit and are never overwritten — they are what tells us
 * the source of the many visits that carry no parameters at all, and how long
 * the visitor took to buy.
 */
export interface UtmParams {
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmTerm?: string
  utmContent?: string
  gclid?: string
  referrer?: string
  landingPage?: string
  firstVisitAt?: string
}

const UTM_COOKIE_NAME = 'mv_utm'
// Matches the 90-day click-through window of the Google Ads conversions.
const UTM_COOKIE_MAX_AGE = 60 * 60 * 24 * 90
const UTM_COOKIE_SHARED_DOMAIN = 'matevolno.cz'

const QUERY_PARAM_TO_FIELD: Record<string, keyof UtmParams> = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_term: 'utmTerm',
  utm_content: 'utmContent',
  gclid: 'gclid',
}

const STORED_FIELDS: readonly (keyof UtmParams)[] = [
  ...Object.values(QUERY_PARAM_TO_FIELD),
  'referrer',
  'landingPage',
  'firstVisitAt',
]

/**
 * Reads the campaign params from the current URL and writes the `mv_utm`
 * cookie, keeping the referrer and the landing page of the first visit. Call
 * once at app boot.
 */
export function captureUtmParams(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  const stored = readUtmParams()
  const searchParams = new URLSearchParams(window.location.search)
  const params: UtmParams = {}

  for (const [queryParam, field] of Object.entries(QUERY_PARAM_TO_FIELD)) {
    const value = searchParams.get(queryParam)
    if (value) params[field] = value
  }

  // No campaign params — the earlier visit is already remembered, keep it as it is.
  if (Object.keys(params).length === 0 && Object.keys(stored).length > 0) return

  // The gclid ties the conversion to the ad click for Google Ads itself, so a
  // later campaign visit without one (a newsletter) must not erase it.
  if (!params.gclid && stored.gclid) params.gclid = stored.gclid

  const referrer = stored.referrer ?? readExternalReferrer()
  if (referrer) params.referrer = referrer
  params.landingPage = stored.landingPage ?? readLandingPage()
  // A cookie from before this field existed keeps it unknown — the time of
  // a later visit would pass for the first one.
  const firstVisitAt = Object.keys(stored).length === 0 ? new Date().toISOString() : stored.firstVisitAt
  if (firstVisitAt) params.firstVisitAt = firstVisitAt

  const cookieValue = encodeURIComponent(JSON.stringify(params))
  document.cookie = `${UTM_COOKIE_NAME}=${cookieValue}; max-age=${UTM_COOKIE_MAX_AGE}; path=/;${cookieDomainAttribute()} SameSite=Lax`
}

/**
 * The referrer is worth storing only when the visitor came from outside the
 * platform. Browsers withhold it entirely for links opened from apps, so its
 * absence does not mean the visit was direct.
 */
function readExternalReferrer(): string | undefined {
  if (!document.referrer) return undefined

  try {
    const host = new URL(document.referrer).hostname
    if (host === window.location.hostname) return undefined
    if (host === UTM_COOKIE_SHARED_DOMAIN || host.endsWith(`.${UTM_COOKIE_SHARED_DOMAIN}`)) {
      return undefined
    }
    return document.referrer
  } catch {
    return undefined
  }
}

/** The hash carries no attribution and would only make the value harder to read. */
function readLandingPage(): string {
  return `${window.location.origin}${window.location.pathname}${window.location.search}`
}

/**
 * Shares the cookie with the other hosts of the platform — namely the partner
 * portal, which reads it when a sign-up is submitted. Empty on localhost and in
 * the native apps, where there is nothing to share it with.
 */
function cookieDomainAttribute(): string {
  return window.location.hostname.endsWith(UTM_COOKIE_SHARED_DOMAIN) ? ` domain=.${UTM_COOKIE_SHARED_DOMAIN};` : ''
}

/**
 * Reads the `mv_utm` cookie captured by `captureUtmParams`. Returns an empty
 * object when absent, unparsable, or in non-browser contexts.
 */
export function readUtmParams(): UtmParams {
  if (typeof document === 'undefined') return {}

  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${UTM_COOKIE_NAME}=([^;]*)`))
  if (match?.[1] === undefined) return {}

  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(match[1]))
    if (typeof parsed !== 'object' || parsed === null) return {}

    const result: UtmParams = {}
    for (const field of STORED_FIELDS) {
      const value = (parsed as Record<string, unknown>)[field]
      if (typeof value === 'string') result[field] = value
    }
    return result
  } catch {
    return {}
  }
}
